import { useState, useEffect, useRef, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import api from '../services/api.js';

const POLL_MS = 60_000;

function timeAgo(iso) {
  const diff = Date.now() - new Date(iso).getTime();
  const m = Math.floor(diff / 60000);
  if (m < 1) return 'just now';
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  return `${Math.floor(h / 24)}d ago`;
}

export default function NotificationBell() {
  const navigate = useNavigate();
  const [count, setCount]             = useState(0);
  const [open, setOpen]               = useState(false);
  const [notifications, setNotifs]    = useState([]);
  const [loadingList, setLoadingList] = useState(false);
  // Panel opens upward with dynamic height, so it's anchored by `bottom`
  // (viewport-relative), never `top` — a `top` anchor would need the panel's
  // height, which changes when Loading… swaps to the list.
  const [pos, setPos]                 = useState({ bottom: 0, left: 0, maxH: 420 });
  const dropRef  = useRef(null); // bell button wrapper (stays in sidebar/drawer)
  const panelRef = useRef(null); // portaled panel — outside dropRef's DOM subtree

  // ── Poll unread count ──────────────────────────────────────────────────────
  const pollCount = useCallback(async () => {
    try {
      const r = await api.get('/api/notifications/unread-count');
      setCount(r.data.count ?? 0);
    } catch { /* non-fatal */ }
  }, []);

  useEffect(() => {
    pollCount();
    const t = setInterval(pollCount, POLL_MS);
    return () => clearInterval(t);
  }, [pollCount]);

  // ── Close on outside click + Escape ───────────────────────────────────────
  // Native mousedown containment must check BOTH refs: the portaled panel is
  // not a DOM descendant of dropRef, so checking dropRef alone would treat
  // every click inside the panel as "outside" and close it instantly.
  useEffect(() => {
    if (!open) return;
    const handler = (e) => {
      if (dropRef.current?.contains(e.target)) return;
      if (panelRef.current?.contains(e.target)) return;
      setOpen(false);
    };
    const onKey = (e) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', handler);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', handler);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  // ── Load notifications when opened ────────────────────────────────────────
  const openBell = async () => {
    if (open) { setOpen(false); return; }
    const r = dropRef.current.getBoundingClientRect();
    const vw = window.innerWidth;
    const panelW = Math.min(320, vw - 24);
    setPos({
      bottom: window.innerHeight - r.top + 6,
      // clamp so the panel never overflows either screen edge (mobile drawer
      // bell sits near the right edge — raw r.left would push it off-screen)
      left: Math.max(12, Math.min(r.left, vw - panelW - 12)),
      // never taller than the space above the bell
      maxH: Math.min(420, r.top - 18),
    });
    setOpen(true);
    setLoadingList(true);
    try {
      const r = await api.get('/api/notifications?limit=10');
      setNotifs(r.data.notifications ?? []);
      setCount(r.data.unreadCount ?? 0);
    } catch { /* non-fatal */ }
    finally { setLoadingList(false); }
  };

  // ── Mark one read + navigate ───────────────────────────────────────────────
  const handleClick = async (n) => {
    setOpen(false);
    if (!n.IsRead) {
      try { await api.patch(`/api/notifications/${n.Id}/read`); } catch { /* non-fatal */ }
      setCount(c => Math.max(0, c - 1));
    }
    if (n.ActionUrl) navigate(n.ActionUrl);
  };

  // ── Mark all read ─────────────────────────────────────────────────────────
  const markAllRead = async (e) => {
    e.stopPropagation();
    try { await api.patch('/api/notifications/read-all'); } catch { /* non-fatal */ }
    setNotifs(ns => ns.map(n => ({ ...n, IsRead: true })));
    setCount(0);
  };

  const urgentStyle = { borderLeft: '3px solid var(--color-danger)' };
  const unreadStyle = { background: 'var(--color-bg-secondary)' };

  return (
    <div ref={dropRef} style={{ position: 'relative', flexShrink: 0 }}>
      {/* Bell button */}
      <button
        onClick={openBell}
        title="Notifications"
        aria-label={`Notifications${count > 0 ? `, ${count} unread` : ''}`}
        style={{
          position: 'relative', background: 'none', border: 'none',
          cursor: 'pointer', color: 'rgba(248,243,236,0.65)', fontSize: '0.9rem',
          padding: '2px 4px', lineHeight: 1, flexShrink: 0,
        }}
      >
        🔔
        {count > 0 && (
          <span style={{
            position: 'absolute', top: -4, right: -4,
            background: 'var(--color-danger)', color: 'var(--color-accent-btn-text)',
            borderRadius: '50%', fontSize: '0.6rem', fontWeight: 700,
            minWidth: 14, height: 14, display: 'flex',
            alignItems: 'center', justifyContent: 'center',
            lineHeight: 1, padding: '0 2px',
          }}>
            {count > 99 ? '99+' : count}
          </span>
        )}
      </button>

      {/* Dropdown — portaled to document.body so no sidebar/drawer ancestor
          (overflow, transform, stacking context) can ever clip it (BUG-BELL-3) */}
      {open && createPortal(
        <div ref={panelRef} style={{
          position: 'fixed', bottom: pos.bottom, left: pos.left,
          width: 'min(320px, calc(100vw - 24px))', maxHeight: pos.maxH, overflowY: 'auto',
          background: 'var(--color-bg-primary)',
          border: '1px solid var(--color-border)',
          borderRadius: 8, boxShadow: '0 8px 24px rgba(0,0,0,0.35)',
          zIndex: 9999,
        }}>
          {/* Header */}
          <div style={{
            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            padding: '10px 12px 8px',
            borderBottom: '1px solid var(--color-border)',
          }}>
            <span style={{ fontWeight: 700, fontSize: '0.8rem', color: 'var(--color-text-primary)' }}>
              Notifications
            </span>
            {count > 0 && (
              <button onClick={markAllRead} style={{
                background: 'none', border: 'none', cursor: 'pointer',
                color: 'var(--color-accent)', fontSize: '0.7rem', fontWeight: 600,
                padding: 0,
              }}>
                Mark all read
              </button>
            )}
          </div>

          {/* List */}
          {loadingList ? (
            <div style={{ padding: '16px 12px', fontSize: '0.8rem', color: 'var(--color-text-muted)', textAlign: 'center' }}>
              Loading…
            </div>
          ) : notifications.length === 0 ? (
            <div style={{ padding: '16px 12px', fontSize: '0.8rem', color: 'var(--color-text-muted)', textAlign: 'center' }}>
              No notifications
            </div>
          ) : (
            notifications.map(n => (
              <div
                key={n.Id}
                onClick={() => handleClick(n)}
                style={{
                  padding: '10px 12px',
                  borderBottom: '1px solid var(--color-border)',
                  cursor: n.ActionUrl ? 'pointer' : 'default',
                  ...(n.Priority === 'urgent' ? urgentStyle : {}),
                  ...(!n.IsRead ? unreadStyle : {}),
                }}
              >
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 8 }}>
                  <span style={{ fontWeight: n.IsRead ? 400 : 600, fontSize: '0.78rem', color: 'var(--color-text-primary)', lineHeight: 1.3 }}>
                    {n.Title}
                  </span>
                  {!n.IsRead && (
                    <span style={{ width: 7, height: 7, borderRadius: '50%', background: 'var(--color-accent)', flexShrink: 0, marginTop: 3 }} />
                  )}
                </div>
                {n.Body && (
                  <div style={{ fontSize: '0.72rem', color: 'var(--color-text-muted)', marginTop: 2, lineHeight: 1.4 }}>
                    {n.Body}
                  </div>
                )}
                <div style={{ fontSize: '0.68rem', color: 'var(--color-text-label)', marginTop: 4 }}>
                  {timeAgo(n.CreatedAt)}
                </div>
              </div>
            ))
          )}
        </div>,
        document.body
      )}
    </div>
  );
}
